import axios from "axios";
import logError from "commons/utils/logError";
import Util from "./Util";
import { logout, getToken, TOKEN_KEY, REFRESH_TOKEN_KEY, REMEMBER_ME } from "services/Auth";
import pathAfterLogin from "services/pathAfterLogin";

const ApiService = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
})

ApiService.interceptors.request.use(function (config) {
  const token = getToken();
  config.headers.Authorization = `Bearer ${token}`;

  return config;
});

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (resolve, reject) => {
  refreshSubscribers.push({ resolve, reject });
};

const notifySubscribers = (newToken) => {
  refreshSubscribers.forEach(({ resolve }) => resolve(newToken));
  refreshSubscribers = [];
};

const rejectSubscribers = (error) => {
  refreshSubscribers.forEach(({ reject }) => reject(error));
  refreshSubscribers = [];
};

ApiService.interceptors.response.use((response) => {
  return response;
}, async (error) => {
  if (error.message === 'Network Error') return;
  if (!error.response) return Promise.reject(error);

  const accessToken = getToken();
  const rememberMeEnabled = localStorage.getItem(REMEMBER_ME);
  const originalRequest = error.config;

  if (
    rememberMeEnabled === 'true' &&
    error.response.status === 401 &&
    accessToken &&
    originalRequest.url !== '/refreshToken' &&
    !originalRequest._retry
  ) {
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh(
          (newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(ApiService.request(originalRequest));
          },
          (err) => reject(err)
        );
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const success = await doRefreshToken();
    isRefreshing = false;

    if (success) {
      const newToken = getToken();
      notifySubscribers(newToken);
      return ApiService.request(originalRequest);
    }

    rejectSubscribers(error);
  }

  if (error.response.status === 401) {
    if (typeof window !== "undefined") {
      pathAfterLogin.setPath(
        window.location.pathname,
        false,
        window.location.search || ""
      );
    }
    await logout();
    window.location = '/login';
    return;
  }

  return error.response;
});

const doRefreshToken = async () => {
  const util = new Util();
  const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  const body = {
    subdomain: util.getSubDomain(),
    refreshToken: storedRefreshToken
  };

  try {
    const response = await ApiService.post('/refreshToken', body);
    if (response?.status === 200 && response.data?.token) {
      localStorage.setItem(TOKEN_KEY, response.data.token);
      localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken);
      import("./notification/PushDeviceService")
        .then((m) => {
          if (
            typeof window !== "undefined" &&
            typeof window.__BEEDOO_ONESIGNAL_ID__ === "string" &&
            window.__BEEDOO_ONESIGNAL_ID__.length > 0
          ) {
            return m.default.touch();
          }
        })
        .catch((err) => logError("ApiService.refreshToken.touch", err));
      return true;
    }
    return false;
  } catch (err) {
    return false;
  }
};

export default ApiService;
